// Trade-secret fabrics: a fabric whose composition is a trade secret shows its "label wording" instead of the full
// composition to people who can't see fibre composition, and on every label for everyone. The composition is written
// in several ways around Falcon ("Cotton: 88%, Nylon: 12%", "88% Cotton / 12% Nylon", "88% Cotton · 12% Nylon",
// "Cotton 88%, Nylon 12%"); each of them is replaced, wherever it appears in a text, by the fabric's label wording.
import { FABRIC_COMP_FIELDS } from "./items.js";

export interface SecretFabric { code: string; composition: Record<string, number>; wording: string; forms: string[] }

export const SECRET_HELP = "This is printed on labels and shown to people who cannot see the full composition.";
export const COMPOSITION_PERMISSION_HELP = "Lets this user see the full composition of trade-secret fabrics.";
export const CONFIDENTIAL_FOOTER = "Contains confidential composition";

/** Is this fabric's composition a trade secret? */
export const isSecretFabric = (d: any): boolean => d?.compositionSecret === true;
/** What labels print, and what people who can't see composition read, for a trade-secret fabric. */
export const labelWordingOf = (d: any): string => String(d?.labelWording ?? "").trim();

/** Every way Falcon writes this composition (longest first, so a longer form is replaced before a shorter one inside it). */
export function compositionForms(c: Record<string, unknown> | undefined | null): string[] {
  const parts = FABRIC_COMP_FIELDS.map(([k, l]) => [l, Number(c?.[k]) || 0] as const).filter(([, v]) => v > 0);
  if (!parts.length) return [];
  const big = [...parts].sort((x, y) => y[1] - x[1]);
  const forms = [
    parts.map(([l, v]) => `${l}: ${v}%`).join(", "),
    big.map(([l, v]) => `${v}% ${l}`).join(" / "),
    big.map(([l, v]) => `${v}% ${l}`).join(" · "),
    parts.map(([l, v]) => `${l} ${v}%`).join(", "),
    big.map(([l, v]) => `${v}% ${l}`).join(", "),
  ];
  return [...new Set(forms)].sort((x, y) => y.length - x.length);
}

export function secretFabric(code: string, data: any): SecretFabric {
  const composition = (data?.composition || {}) as Record<string, number>;
  return { code, composition, wording: labelWordingOf(data), forms: compositionForms(composition) };
}

const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A text with each secret composition replaced by its label wording (an empty wording removes it, with its separator). */
export function redactText(text: string, secrets: SecretFabric[]): string {
  if (!text || !secrets.length) return text;
  let out = text;
  for (const s of secrets) {
    for (const f of s.forms) {
      if (!out.includes(f)) continue;
      if (s.wording) out = out.split(f).join(s.wording);
      else out = out.replace(new RegExp(`(?:\\s*(?:,|/|·|\\|)\\s*)?${escRe(f)}`, "g"), "").replace(/^\s*(?:,|\/|·)\s*/, "");
    }
  }
  return out;
}

/** Is any secret composition in this text? */
export const containsSecret = (text: string, secrets: SecretFabric[]) => secrets.some((s) => s.forms.some((f) => text.includes(f)));

/** A raw composition object ({ cotton: 88, nylon: 12 }) that is one of the secrets. */
function isSecretMap(v: unknown, secrets: SecretFabric[]): boolean {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const keys = Object.keys(v as object);
  if (!keys.length || !keys.every((k) => FABRIC_COMP_FIELDS.some(([f]) => f === k))) return false;
  const forms = compositionForms(v as Record<string, unknown>);
  return !!forms.length && secrets.some((s) => s.forms[0] === forms[0] || s.forms.includes(forms[0]));
}

/** Any value (an API reply, a stored snapshot) with every secret composition replaced; raw secret compositions removed. */
export function redactDeep<T>(value: T, secrets: SecretFabric[]): T {
  if (!secrets.length) return value;
  const walk = (v: any): any => {
    if (typeof v === "string") return redactText(v, secrets);
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === "object") {
      const out: any = {};
      for (const [k, x] of Object.entries(v)) {
        if ((k === "composition" || k === "proposedComposition") && isSecretMap(x, secrets)) { out[k] = {}; continue; }
        out[k] = walk(x);
      }
      return out;
    }
    return v;
  };
  return walk(value);
}

/** Split a CSV text into rows of cells (quoted cells, doubled quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; } else if (c === '"') q = false; else cell += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

/** Label data (a sealed Tech Pack copy, a frozen label pack, a QC reference): every label prints the label wording of a
 *  trade-secret fabric, for everyone; the other languages' composition (and a description with fibres in it) is left blank. */
export function redactLabelData<T>(data: T, secrets: SecretFabric[]): T {
  if (!data || !secrets.length || !containsSecret(JSON.stringify(data), secrets)) return data;
  const out: any = redactDeep(data, secrets);
  const fixI18n = (o: any): void => {
    if (!o || typeof o !== "object") return;
    if (o.i18n && typeof o.i18n === "object" && !Array.isArray(o.i18n)) {
      for (const [k, v] of Object.entries<any>(o.i18n)) if (v && typeof v === "object") o.i18n[k] = { ...v, composition: "", description: /\d%/.test(String(v.description || "")) ? "" : v.description };
    }
    for (const x of Object.values(o)) if (x && typeof x === "object") fixI18n(x);
  };
  fixI18n(out);
  return out;
}
